import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useInView } from 'react-intersection-observer';
import { useTranslation } from 'react-i18next';
import { useAppContext } from '../../hooks/useAppContext';
import './styles.css';

const UserTypeSection = () => {
  const { t } = useTranslation();
  const { language } = useAppContext();
  
  const userProfiles = [
    {
      id: 1,
      type: language === 'ar' ? 'الشباب' : 'Jeunes',
      description: language === 'ar' 
        ? 'برنامج الفرصة الجديدة مخصص للشباب غير المنخرطين في التعليم أو التوظيف أو التدريب (NEET) الذين يرغبون في فهم احتياجاتهم وتطوير مهاراتهم. إذا كنت في حيرة من أمرك بشأن مستقبلك، فهذا البرنامج لك.'
        : 'Le DNC est dédié aux jeunes NEET (ni en études, ni en emploi, ni en formation) qui souhaitent comprendre leurs besoins et développer leurs compétences. Si vous êtes dans le doute concernant votre avenir, ce programme est fait pour vous.',
      icon: 'fa-user-graduate',
      actionLink: '/registration',
      actionText: language === 'ar' ? 'سجل الآن' : 'S\'inscrire',
      color: '#0f4c81',
      gradient: 'linear-gradient(90deg, #0f4c81, #0f4c81)',
      iconColor: 'white'
    },
    {
      id: 2,
      type: language === 'ar' ? 'الآباء' : 'Parents',
      description: language === 'ar' 
        ? 'برنامج يقدم الدعم للآباء الذين يرغبون في مساعدة أبنائهم على تحديد مسارهم المهني. إذا كنت قلقًا بشأن مستقبل ابنك، نحن هنا لمرافقتهم.'
        : 'Programme qui vient en appui aux parents qui souhaitent aider leurs enfants à définir leur parcours professionnel. Si vous êtes inquiet pour l\'avenir de votre enfant, nous sommes là pour les accompagner.',
      icon: 'fa-users',
      actionLink: '/pedagogy',
      actionText: language === 'ar' ? 'اكتشف المزيد' : 'Découvrir',
      color: '#ffd166',
      gradient: 'linear-gradient(90deg, #ffd166, #ffd166)',
      iconColor: '#0f4c81'
    },
    {
      id: 3,
      type: language === 'ar' ? 'الشركاء' : 'Partenaires',
      description: language === 'ar' 
        ? 'برنامج الفرصة الجديدة مفتوح للشركاء المهتمين بدعم مبادراتنا والمساهمة في تطوير مهارات الشباب. إذا كنت ترغب في أن تصبح شريكًا لنا، تواصل معنا.'
        : 'Le DNC est ouvert aux partenaires intéressés par le soutien de nos initiatives et la contribution au développement des compétences des jeunes. Si vous souhaitez devenir partenaire, contactez-nous.',
      icon: 'fa-handshake',
      actionLink: '/contact',
      actionText: language === 'ar' ? 'تواصل معنا' : 'Nous contacter',
      color: '#0f4c81',
      gradient: 'linear-gradient(90deg, #0f4c81, #0f4c81)',
      iconColor: 'white'
    }
  ];

  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const cardVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 15
      }
    }
  };

  return (
    <section className="user-type-section" ref={ref}>
      <div className="container">
        <motion.div
          className="section-header"
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.8 }}
        >
          <h2>{language === 'ar' ? 'اختر مسارك' : 'Choisissez votre parcours'}</h2>
          <p>{language === 'ar' ? 'برامج مخصصة لمختلف الفئات' : 'Des programmes adaptés à différents profils'}</p>
        </motion.div>

        <motion.div
          className="user-profiles-container"
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          variants={containerVariants}
        >
          {userProfiles.map((profile, index) => (
            <motion.div
              key={profile.id}
              className="user-profile-card-wrapper"
              variants={cardVariants}
            >
              <motion.div 
                className="user-profile-card"
                initial={{ opacity: 0, y: 30 }}
                animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
                transition={{ duration: 0.5, delay: index * 0.2 }}
                style={{ 
                  boxShadow: `0 10px 30px rgba(${profile.id % 2 === 0 ? '255, 209, 102' : '15, 76, 129'}, 0.15)`,
                  borderTop: `4px solid ${profile.id % 2 === 0 ? '#ffd166' : '#0f4c81'}`
                }}
              >
                <div 
                  className="profile-icon"
                  style={{ 
                    background: profile.gradient,
                    boxShadow: `0 10px 20px ${profile.color}33`
                  }}
                >
                  <i className={`fas ${profile.icon}`} style={{ color: profile.iconColor }}></i>
                </div>
                <h3 
                  className="profile-type"
                  style={{ 
                    color: profile.color
                  }}
                >
                  {profile.type}
                </h3>
                <p className="profile-description">
                  {profile.description}
                </p>
                <div className="profile-action-container">
                  <Link 
                    to={profile.actionLink} 
                    className={profile.id % 2 === 0 ? "btn-secondary" : "btn-primary"}
                    style={{ 
                      boxShadow: `0 5px 15px ${profile.color}33`
                    }}
                  >
                    {profile.actionText}
                  </Link>
                </div>
              </motion.div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default UserTypeSection;
